import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Plus, Trash2, X, Play, FileText, Layers, Search, Star, Link2 } from "lucide-react";
import { inferContent, kindToBucket } from "../lib/contentKind";
import useUrlState from "../hooks/useUrlState";
import useShortcuts from "../hooks/useShortcuts";
import AvatarChip from "../components/ui/avatar-chip";
import { EmptyState } from "../components/ui/empty-state";
import { computeSections, splitTagChips } from "../lib/librarySections";
import Hint from "../components/Hint";

/**
 * Library — videos, decks, docs. Anything worth watching twice.
 *
 * Ive-minimal. One search bar. Watch / Read toggle. Paste-a-URL add.
 * No categories, no likes, no approval. Flat team-wide shelf.
 */

function Thumb({ url, title, kind }) {
  const [idx, setIdx] = useState(0);
  const encoded = encodeURIComponent(url || "");
  const sources = [
    `https://api.microlink.io/?url=${encoded}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1200&viewport.height=900`,
    `https://image.thum.io/get/width/800/crop/450/${url}`,
    `https://s0.wp.com/mshots/v1/${encoded}?w=800&h=450`,
  ];
  const onErr = () => setIdx(idx < sources.length - 1 ? idx + 1 : -1);
  if (idx === -1 || !url) {
    const hue = [...(title || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
    const Icon = kind === "video" ? Play : kind === "deck" ? Layers : FileText;
    return (
      <div style={{
        width: "100%",
        height: "100%",
        background: `linear-gradient(135deg, hsla(${hue}, 50%, 45%, .25), hsla(${hue}, 50%, 30%, .5))`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Icon size={48} style={{ color: `hsla(${hue}, 60%, 75%, .9)`, opacity: .5 }} strokeWidth={1.2} />
      </div>
    );
  }
  return (
    <img
      src={sources[idx]}
      alt={title || ""}
      loading="lazy"
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      onError={onErr}
    />
  );
}

export default function PitchDeckLibrary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = user?.user_id || user?.id;
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUrlState("q", "");
  // Single-select "personal/meta" axis. All four are orthogonal ways to narrow
  // the grid — starred, my-uploads, videos, or docs/decks. Empty = no filter.
  const [personal, setPersonal] = useUrlState("filter", ""); // "" | starred | mine | watch | read
  // Multi-select tag filter. URL shape: ?tag=gtm&tag=research — we roll it to
  // a comma-joined string for useUrlState and split on read. AND semantics
  // client-side matches the backend.
  const [tagsParam, setTagsParam] = useUrlState("tag", "");
  const selectedTags = useMemo(
    () => tagsParam.split(",").map((t) => t.trim()).filter(Boolean),
    [tagsParam]
  );
  const setSelectedTags = (next) => setTagsParam(next.join(","));
  const [showAdd, setShowAdd] = useState(false);
  const [tagCloud, setTagCloud] = useState([]);
  const [allTagsExpanded, setAllTagsExpanded] = useState(false);

  // Legacy compat: old modal URLs looked like /pitch-decks?id=xyz. Redirect to
  // the new /library/:deckId route once so Slack/email links from before the
  // route-conversion still land on the right item.
  useEffect(() => {
    const legacyId = searchParams.get("id");
    if (legacyId) navigate(`/library/${legacyId}`, { replace: true });
  }, [searchParams, navigate]);

  // Load the aggregated tag cloud for the chip row. Refreshes when the grid
  // reloads (so adding/removing tagged items updates counts). Cheap aggregation
  // server-side — happy to call on every mount.
  const loadTags = async () => {
    try {
      const res = await api.get("/pitch-decks/tags");
      setTagCloud(res.data || []);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { loadTags(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/pitch-decks");
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // True when any filter is active — search text, a chip selection, or tags.
  // Drives the "sections vs grid" decision: landing shows sections, filtered
  // state collapses to a flat grid.
  const hasActiveFilter = Boolean(
    (search && search.trim()) || personal || selectedTags.length > 0
  );

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return items.filter((it) => {
      // Personal/meta axis (single-select)
      if (personal === "watch" && kindToBucket(it.kind) !== "watch") return false;
      if (personal === "read" && kindToBucket(it.kind) !== "read") return false;
      if (personal === "starred" && !it.starred_by_me) return false;
      if (personal === "mine" && it.added_by !== uid) return false;
      if (personal === "unwatched" && it.completed_by_me) return false;
      // Tag filter — AND semantics
      if (selectedTags.length > 0) {
        const itemTags = (it.tags || []).map((t) => t.toLowerCase());
        for (const want of selectedTags) {
          if (!itemTags.includes(want)) return false;
        }
      }
      // Free-text search — now matches tags and filenames too, not just
      // title/description/uploader. This is the search-by-what-I-remember
      // fix: people search "excel" or "gtm" and expect hits.
      if (q) {
        const attachmentNames = (it.attachments || [])
          .map((a) => `${a.filename || ""} ${a.label || ""}`)
          .join(" ");
        const tagStr = (it.tags || []).join(" ");
        const hay = `${it.title} ${it.description || ""} ${it.added_by_name || ""} ${tagStr} ${attachmentNames}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, personal, selectedTags, uid]);

  // Sections for the landing view (empty filter state).
  const sections = useMemo(() => computeSections(items, uid), [items, uid]);
  const tagChipSplit = useMemo(() => splitTagChips(tagCloud, 10), [tagCloud]);

  // Optimistic star toggle — flips starred_by_me on the local item immediately,
  // rolls back on server error. Fires from card hover icon + from sections.
  const toggleStar = async (item) => {
    const deckId = item.deck_id;
    const snapshot = items;
    setItems((prev) =>
      prev.map((x) => x.deck_id === deckId
        ? { ...x, starred_by_me: !x.starred_by_me, star_count: (x.star_count || 0) + (x.starred_by_me ? -1 : 1) }
        : x
      )
    );
    try {
      await api.post(`/pitch-decks/${deckId}/star`);
    } catch (e) {
      setItems(snapshot);
      toast.error("Couldn't update star");
    }
  };

  const togglePersonal = (key) => setPersonal(personal === key ? "" : key);
  const toggleTagChip = (tag) => {
    const lower = tag.toLowerCase();
    if (selectedTags.includes(lower)) {
      setSelectedTags(selectedTags.filter((t) => t !== lower));
    } else {
      setSelectedTags([...selectedTags, lower]);
    }
  };
  const clearAllFilters = () => {
    setSearch("");
    setPersonal("");
    setSelectedTags([]);
  };

  useShortcuts([
    { keys: "/", description: "Focus search", group: "Library", handler: () => document.getElementById("library-search")?.focus() },
    { keys: "n", description: "Add", group: "Library", handler: () => setShowAdd(true) },
    { keys: "escape", handler: () => setShowAdd(false) },
  ], { enabled: !loading });

  // Optimistic delete with 5s undo toast — matches the MyDashboard / TasksList /
  // BizDevBriefDetail pattern. Click the trash on a card → disappears immediately,
  // toast gives 5s to undo. Server DELETE fires only on toast auto-close.
  const requestDelete = (item) => {
    const snapshot = items;
    setItems((prev) => prev.filter((x) => x.deck_id !== item.deck_id));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);

    let undone = false;
    toast(`Removed: ${(item.title || "item").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          setItems(snapshot);
          if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(6);
        },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/pitch-decks/${item.deck_id}`); }
          catch (e) {
            console.error("Library delete failed", e);
            setItems(snapshot);
            toast.error("Couldn't remove — restored");
          }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/pitch-decks/${item.deck_id}`); }
          catch (e) {
            console.error("Library delete failed", e);
            setItems(snapshot);
            toast.error("Couldn't remove — restored");
          }
        }, 50);
      },
    });
  };

  return (
    <div className="dm-lib-page dm-page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 32px 120px", fontFamily: "var(--font-sans)" }}>
      {/* Hover-reveal for card action buttons. Starred items keep the star
          visible always (gold fill = personal pin, worth the chrome); unstarred
          stars and all delete buttons only appear on hover/focus. */}
      <style>{`
        .dm-lib-card .dm-lib-card-delete { opacity: 0; }
        .dm-lib-card:hover .dm-lib-card-delete,
        .dm-lib-card:focus-within .dm-lib-card-delete { opacity: 1; }
        .dm-lib-card:hover .dm-lib-card-star,
        .dm-lib-card:focus-within .dm-lib-card-star { opacity: 1; }
      `}</style>
      {/* Header — generous whitespace, thin hierarchy */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 34,
          fontWeight: 600,
          color: "var(--t1)",
          letterSpacing: "-0.025em",
          margin: 0,
          lineHeight: 1.1,
        }}>
          Library
          <Hint id="library.intro">
            Drop in any deck, doc, or Loom worth re-watching. Tag with #content-ideas / #research / #process so others can find it. Star the ones you want quick access to.
          </Hint>
        </h1>
        <p style={{ color: "var(--t3)", fontSize: 14, marginTop: 8, marginBottom: 0 }}>
          Videos, decks, docs. Anything worth watching twice.
        </p>
      </div>

      {/* Top row: search (full width) + Add. The old segmented control is gone
          — its Watch/Read buckets moved into the chip row below, freeing real
          estate for the search box and creating a single coherent filter
          surface. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
          <Search size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--t3)" }} />
          <input
            id="library-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, description, tags, or files"
            style={{
              width: "100%",
              padding: "11px 14px 11px 38px",
              background: "var(--s1)",
              border: "1px solid var(--bdr)",
              borderRadius: 999,
              color: "var(--t1)",
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              outline: "none",
              transition: "all .15s",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--bdrh)";
              e.currentTarget.style.background = "var(--bg)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "var(--bdr)";
              e.currentTarget.style.background = "var(--s1)";
            }}
          />
        </div>
        <button
          onClick={() => setShowAdd(true)}
          title="Add to Library (press n)"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 18px",
            background: "var(--t1)",
            color: "var(--bg)",
            border: "none",
            borderRadius: 999,
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            transition: "transform .1s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = ""}
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {/* Chip row: personal/meta chips on the left, tag chips on the right
          separated by a subtle divider. Single scrollable line — no wrap, no
          segmented control, no checkbox sidebars. Clicking a chip toggles it.
          Empty-shelf friendly: personal chips stay visible (they're always
          meaningful), tag chips collapse to nothing when the tag cloud is
          empty. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 32,
          overflowX: "auto",
          paddingBottom: 4,
          // Hide the scrollbar on the chip row — horizontal scroll is a fallback
          // for overflow, not a first-class UX.
          scrollbarWidth: "thin",
        }}
      >
        <Chip
          active={personal === "starred"}
          onClick={() => togglePersonal("starred")}
          icon={<Star size={12} fill={personal === "starred" ? "#fbbf24" : "none"} />}
          label="Starred"
        />
        <Chip
          active={personal === "unwatched"}
          onClick={() => togglePersonal("unwatched")}
          label="Unwatched"
        />
        <Chip
          active={personal === "mine"}
          onClick={() => togglePersonal("mine")}
          label="Your uploads"
        />
        <Chip
          active={personal === "watch"}
          onClick={() => togglePersonal("watch")}
          label="Watch"
        />
        <Chip
          active={personal === "read"}
          onClick={() => togglePersonal("read")}
          label="Read"
        />

        {tagChipSplit.total > 0 && (
          <span style={{ width: 1, height: 20, background: "var(--bdr)", margin: "0 4px", flexShrink: 0 }} />
        )}

        {tagChipSplit.visible.map((tc) => (
          <Chip
            key={tc.tag}
            active={selectedTags.includes(tc.tag)}
            onClick={() => toggleTagChip(tc.tag)}
            label={`#${tc.tag}`}
            badge={tc.count}
          />
        ))}

        {tagChipSplit.overflow.length > 0 && (
          <TagOverflowMenu
            overflow={tagChipSplit.overflow}
            selectedTags={selectedTags}
            onToggle={toggleTagChip}
            expanded={allTagsExpanded}
            setExpanded={setAllTagsExpanded}
          />
        )}

        {hasActiveFilter && (
          <>
            <span style={{ flex: 1 }} />
            <button
              onClick={clearAllFilters}
              style={{
                flexShrink: 0,
                background: "transparent",
                border: "none",
                color: "var(--t3)",
                fontSize: 12,
                cursor: "pointer",
                padding: "4px 8px",
                fontFamily: "var(--font-sans)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              Clear
            </button>
          </>
        )}
      </div>

      {/* Render path — three states:
            1. Loading: quiet "Loading…" line
            2. No filter active + items exist: sections (landing view)
            3. Filter active OR no items: flat grid (or empty state) */}
      {loading ? (
        <div style={{ color: "var(--t3)", padding: "40px 0", textAlign: "center" }}>Loading…</div>
      ) : !hasActiveFilter && items.length > 0 ? (
        <SectionsView
          sections={sections}
          allItems={items}
          onOpen={(it) => navigate(`/library/${it.deck_id}`)}
          onToggleStar={toggleStar}
          onRequestDelete={requestDelete}
          uid={uid}
          isAdmin={isAdmin}
          onSectionCta={(href) => { if (href) navigate(`/pitch-decks${href}`); }}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Layers}
          title={hasActiveFilter ? "Nothing matches" : "The shelf is empty"}
          message={hasActiveFilter ? "Try a different filter or clear." : "Add the first thing. A Loom, a deck, a good read."}
          cta={!hasActiveFilter ? { label: "Add something", onClick: () => setShowAdd(true) } : undefined}
        />
      ) : (
        <div className="dm-lib-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 24,
        }}>
          {filtered.map((it) => (
            <Card
              key={it.deck_id}
              item={it}
              canDelete={isAdmin || it.added_by === uid}
              onOpen={() => navigate(`/library/${it.deck_id}`)}
              onRequestDelete={() => requestDelete(it)}
              onToggleStar={() => toggleStar(it)}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onAdded={(newItem) => {
            setItems((prev) => [newItem, ...prev]);
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════ Chip (filter row pill) ═══════════════════════ */

function Chip({ active, onClick, icon, label, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${active ? "var(--t1)" : "var(--bdr)"}`,
        background: active ? "var(--t1)" : "var(--s1)",
        color: active ? "var(--bg)" : "var(--t2)",
        fontFamily: "var(--font-sans)",
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        transition: "all .15s",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span style={{
          fontSize: 10,
          fontWeight: 600,
          opacity: active ? 0.75 : 0.5,
          minWidth: 14,
          textAlign: "center",
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

/* ═══════════════════════ Tag overflow menu ═══════════════════════ */

function TagOverflowMenu({ overflow, selectedTags, onToggle, expanded, setExpanded }) {
  const ref = useRef(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (!expanded) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setExpanded(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [expanded, setExpanded]);

  const matched = overflow.filter((t) =>
    !search.trim() || t.tag.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: "6px 12px",
          borderRadius: 999,
          border: "1px dashed var(--bdr)",
          background: "transparent",
          color: "var(--t3)",
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        +{overflow.length} more
      </button>
      {expanded && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: "var(--s1)",
            border: "1px solid var(--bdr)",
            borderRadius: 12,
            padding: 10,
            width: 280,
            maxHeight: 340,
            overflowY: "auto",
            zIndex: 50,
            boxShadow: "0 16px 40px rgba(0,0,0,.3)",
          }}
        >
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a tag"
            style={{
              width: "100%",
              padding: "8px 10px",
              background: "var(--bg)",
              border: "1px solid var(--bdr)",
              borderRadius: 8,
              color: "var(--t1)",
              fontSize: 13,
              outline: "none",
              marginBottom: 8,
              fontFamily: "var(--font-sans)",
            }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {matched.length === 0 ? (
              <span style={{ color: "var(--t3)", fontSize: 12, padding: 6 }}>No matches</span>
            ) : matched.map((tc) => (
              <Chip
                key={tc.tag}
                active={selectedTags.includes(tc.tag)}
                onClick={() => onToggle(tc.tag)}
                label={`#${tc.tag}`}
                badge={tc.count}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════ Sections view (landing) ═══════════════════════ */

/**
 * Horizontal-strip sections on the landing (unfiltered) view. Parent renders
 * an "All" grid directly after this for browsing every item — sections above
 * are shortcuts, not alternatives.
 */
function SectionsView({ sections, allItems, onOpen, onToggleStar, onRequestDelete, uid, isAdmin, onSectionCta }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
      {sections.map((sec) => (
        <section key={sec.id}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.01em", margin: 0 }}>
              {sec.title}
            </h2>
            <span style={{ fontSize: 12, color: "var(--t3)" }}>{sec.count}</span>
            <span style={{ flex: 1 }} />
            {sec.href && (
              <button
                type="button"
                onClick={() => onSectionCta(sec.href)}
                style={{ background: "transparent", border: "none", color: "var(--t3)", fontSize: 12, cursor: "pointer", padding: "4px 8px", fontFamily: "var(--font-sans)" }}
              >
                See all →
              </button>
            )}
          </div>
          <div className="dm-lib-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {sec.items.map((it) => (
              <Card
                key={it.deck_id}
                item={it}
                canDelete={isAdmin || it.added_by === uid}
                onOpen={() => onOpen(it)}
                onRequestDelete={() => onRequestDelete(it)}
                onToggleStar={() => onToggleStar(it)}
              />
            ))}
          </div>
        </section>
      ))}

      {/* All — full shelf below the section strips. Users who know exactly
          what they want can scan it; users who want curated surfaces stay
          above in the sections. */}
      <section>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.01em", margin: 0 }}>
            All
          </h2>
          <span style={{ fontSize: 12, color: "var(--t3)" }}>{allItems.length}</span>
        </div>
        <div className="dm-lib-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
          {allItems.map((it) => (
            <Card
              key={it.deck_id}
              item={it}
              canDelete={isAdmin || it.added_by === uid}
              onOpen={() => onOpen(it)}
              onRequestDelete={() => onRequestDelete(it)}
              onToggleStar={() => onToggleStar(it)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════ Card ═══════════════════════ */

function Card({ item, onOpen, canDelete, onRequestDelete, onToggleStar }) {
  const inf = inferContent(item.content_url || item.canva_url || "");
  const kind = item.kind || inf.kind;
  const KindIcon = kind === "video" ? Play : kind === "deck" ? Layers : FileText;
  const hasAttachments = (item.attachments || []).length > 0;
  const starred = !!item.starred_by_me;
  const watched = !!item.completed_by_me;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      className="dm-lib-card"
      style={{
        textAlign: "left",
        background: "var(--s1)",
        border: "1px solid var(--bdr)",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all .2s cubic-bezier(.4,0,.2,1)",
        fontFamily: "var(--font-sans)",
        padding: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = "var(--bdrh)";
        e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,.3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.borderColor = "var(--bdr)";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <div style={{ aspectRatio: "16 / 9", width: "100%", background: "var(--s2)", position: "relative", overflow: "hidden" }}>
        <Thumb url={item.content_url || item.canva_url} title={item.title} kind={kind} />

        {/* Watched badge — small always-visible pill top-left so users
            can tell at a glance which items they've already marked as
            watched. Green for positive reinforcement. */}
        {watched && (
          <div
            aria-label="Watched"
            title="You've marked this as watched"
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 8px 3px 6px",
              background: "rgba(34,197,94,.92)",
              borderRadius: 999,
              color: "#062d15",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Watched
          </div>
        )}

        {/* Hover overlay — star + delete, top-right, single consistent spot.
            Star always visible when active (gold fill gives it presence even
            when the overlay is faded); delete only reveals on hover for users
            who can delete. */}
        <div
          className="dm-lib-card-actions"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleStar?.(); }}
            title={starred ? "Unstar" : "Star this item"}
            aria-label={starred ? "Unstar" : "Star"}
            className="dm-lib-card-star"
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              background: starred ? "rgba(251,191,36,.95)" : "rgba(0,0,0,.72)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,.12)",
              color: starred ? "#422006" : "rgba(255,255,255,.85)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity .15s, background .15s, color .15s",
              padding: 0,
              // Starred items show the star always; unstarred only on hover.
              opacity: starred ? 1 : 0,
            }}
          >
            <Star size={13} strokeWidth={2} fill={starred ? "#422006" : "none"} />
          </button>
          {canDelete && (
            <button
              type="button"
              className="dm-lib-card-delete"
              onClick={(e) => { e.stopPropagation(); onRequestDelete?.(); }}
              title="Remove from Library"
              aria-label="Remove from Library"
              style={{
                width: 30,
                height: 30,
                borderRadius: 999,
                background: "rgba(0,0,0,.72)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,.12)",
                color: "rgba(255,255,255,.85)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity .15s, background .15s, color .15s",
                padding: 0,
                opacity: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239,68,68,.9)";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,.72)";
                e.currentTarget.style.color = "rgba(255,255,255,.85)";
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* Body — title + single de-emphasised meta row (kind · by · date · +N) */}
      <div style={{ padding: "14px 16px 14px" }}>
        <div style={{
          fontSize: 15,
          fontWeight: 600,
          color: "var(--t1)",
          letterSpacing: "-0.005em",
          lineHeight: 1.35,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "2.7em",
        }}>
          {item.title}
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginTop: 8,
          fontSize: 12,
          color: "var(--t3)",
          overflow: "hidden",
        }}>
          <KindIcon size={12} strokeWidth={2} style={{ flexShrink: 0, opacity: .7 }} />
          <span style={{ flexShrink: 0 }}>{inf.label}</span>
          {item.added_by_name && (
            <>
              <span style={{ opacity: .4 }}>·</span>
              <AvatarChip name={item.added_by_name} size={14} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {item.added_by_name}
              </span>
            </>
          )}
          {hasAttachments && (
            <>
              <span style={{ opacity: .4, flexShrink: 0 }}>·</span>
              <span style={{ flexShrink: 0 }} title={`${item.attachments.length} supporting material${item.attachments.length === 1 ? "" : "s"}`}>
                +{item.attachments.length}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ Add modal ═══════════════════════ */

function AddModal({ onClose, onAdded }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // File upload state — for docs/Excel/PDF/reading material
  const [uploadedFile, setUploadedFile] = useState(null); // { content_url, filename, size, kind }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Supporting materials — extra attachments staged before submit
  const [attachments, setAttachments] = useState([]); // [{content_url, filename, label, kind}]
  // Tags input — chip-style with autocomplete from the tag cloud.
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  useEffect(() => {
    // Load the existing tag cloud so we can autocomplete against what the team
    // actually uses (prevents fragmentation — "GTM" vs "gtm" vs "go-to-market").
    api.get("/pitch-decks/tags").then((r) => setTagSuggestions(r.data || [])).catch(() => {});
  }, []);

  const effectiveUrl = uploadedFile?.content_url || url;
  const inf = useMemo(() => inferContent(effectiveUrl), [effectiveUrl]);

  // Live preview — resolves og:image + og:title as the user pastes a URL
  // (skipped for file uploads — we already know the kind)
  useEffect(() => {
    if (uploadedFile) return;
    if (!url || url.length < 10) { setThumb(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setDetecting(true);
      try {
        const res = await api.post("/pitch-decks/resolve-thumbnail", { url });
        if (cancelled) return;
        setThumb(res.data?.thumbnail_url || null);
        if (res.data?.og_title && !title) setTitle(res.data.og_title);
      } catch (_) { /* silent */ }
      finally { if (!cancelled) setDetecting(false); }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [url, uploadedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25MB"); return; }
    setUploading(true);
    setUrl(""); // clear URL path if user was typing one
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/pitch-decks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedFile(res.data);
      // Suggest title from filename if none typed yet
      if (!title) {
        const stem = (file.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
        if (stem) setTitle(stem.charAt(0).toUpperCase() + stem.slice(1));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const clearFile = () => { setUploadedFile(null); };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const submit = async () => {
    const finalUrl = (uploadedFile?.content_url || url).trim();
    if (!finalUrl) { toast.error("Paste a URL or upload a file"); return; }
    if (!title.trim()) { toast.error("Give it a title"); return; }
    setSubmitting(true);
    // If the user typed a tag into the draft input and hit Add without pressing
    // Enter first, capture it here so their intent isn't lost.
    const pendingDraft = tagDraft.trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, "-");
    const finalTags = pendingDraft && !tags.includes(pendingDraft) ? [...tags, pendingDraft] : tags;
    try {
      const res = await api.post("/pitch-decks", {
        title: title.trim(),
        content_url: finalUrl,
        description: description.trim(),
        attachments: attachments.length ? attachments : undefined,
        tags: finalTags.length ? finalTags : undefined,
      });
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(6);
      toast.success(attachments.length
        ? `Added with ${attachments.length} material${attachments.length === 1 ? "" : "s"}`
        : "Added to Library");
      onAdded(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add");
    } finally { setSubmitting(false); }
  };

  // Tag input helpers — shared by the chip input and its autocomplete dropdown.
  const addTag = (raw) => {
    const v = (raw || tagDraft).trim().toLowerCase().replace(/^#/, "").replace(/\s+/g, "-");
    if (!v) return;
    if (tags.includes(v)) { setTagDraft(""); return; }
    setTags([...tags, v]);
    setTagDraft("");
  };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));
  const draftMatches = tagDraft.trim()
    ? tagSuggestions.filter((s) => s.tag.includes(tagDraft.trim().toLowerCase()) && !tags.includes(s.tag)).slice(0, 6)
    : [];

  // Staged attachment helpers
  const addAttachmentUrl = () => {
    setAttachments((prev) => [...prev, { content_url: "", filename: null, label: "", kind: null }]);
  };
  const updateAttachment = (i, patch) => {
    setAttachments((prev) => prev.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  };
  const removeAttachment = (i) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
  };
  const uploadAttachmentFile = async (i, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25MB"); return; }
    updateAttachment(i, { _uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/pitch-decks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateAttachment(i, {
        content_url: res.data.content_url,
        filename: res.data.filename,
        kind: res.data.kind,
        _uploading: false,
      });
    } catch (e) {
      updateAttachment(i, { _uploading: false });
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  // Accept multiple files in one go (from file picker OR drop). Creates a new
  // staged-attachment row per file and uploads in parallel.
  const uploadMultipleAttachmentFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const oversize = files.filter((f) => f.size > 25 * 1024 * 1024).map((f) => f.name);
    if (oversize.length) {
      toast.error(`${oversize.length} file${oversize.length === 1 ? "" : "s"} exceed 25MB — skipped`);
    }
    const ok = files.filter((f) => f.size <= 25 * 1024 * 1024);
    if (!ok.length) return;

    // Add placeholder rows
    const baseIdx = attachments.length;
    const placeholders = ok.map(() => ({ content_url: "", filename: null, label: "", kind: null, _uploading: true }));
    setAttachments((prev) => [...prev, ...placeholders]);

    // Upload all in parallel
    await Promise.all(
      ok.map(async (file, n) => {
        const targetIdx = baseIdx + n;
        try {
          const fd = new FormData();
          fd.append("file", file);
          const res = await api.post("/pitch-decks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((prev) => prev.map((a, idx) => idx === targetIdx
            ? { ...a, content_url: res.data.content_url, filename: res.data.filename, kind: res.data.kind, _uploading: false }
            : a
          ));
        } catch (e) {
          setAttachments((prev) => prev.map((a, idx) => idx === targetIdx
            ? { ...a, _uploading: false, _error: "Upload failed" }
            : a
          ));
        }
      })
    );
    toast.success(`${ok.length} file${ok.length === 1 ? "" : "s"} attached`);
  };

  // Drag-and-drop state for the attachments dropzone
  const [attDropOver, setAttDropOver] = useState(false);
  const onAttDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setAttDropOver(false);
    uploadMultipleAttachmentFiles(e.dataTransfer?.files);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.65)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 90,
        animation: "fadeIn .2s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="dm-lib-add-modal"
        style={{
          background: "var(--s1)",
          border: "1px solid var(--bdr)",
          borderRadius: 20,
          width: "100%",
          maxWidth: 560,
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          padding: 32,
          boxShadow: "0 40px 120px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.015em", margin: 0 }}>
            Add to Library
          </h2>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--t3)", padding: 4, display: "flex" }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* HERO: unified drop-and-paste surface.
              Before: separate "paste a link" input → OR divider → dropzone,
              each half-height. Users felt they had to fill a form. Now: one
              big drop target IS the primary surface. A URL input sits inside
              it. Drop a file or paste a link — same place, same gesture. */}
          {!uploadedFile ? (
            <label
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                padding: "32px 20px",
                background: dragOver ? "rgba(96,165,250,.08)" : "var(--bg)",
                border: `1.5px dashed ${dragOver ? "#60a5fa" : "var(--bdr)"}`,
                borderRadius: 14,
                cursor: uploading ? "wait" : "pointer",
                transition: "all .15s",
                textAlign: "center",
                minHeight: 180,
              }}
            >
              {uploading ? (
                <span style={{ fontSize: 14, color: "var(--t2)" }}>Uploading…</span>
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--t1)" }}>
                    Drop a file or paste a link
                  </div>
                  <div style={{ fontSize: 12, color: "var(--t3)" }}>
                    Loom · YouTube · Canva · Google Slides · PDF · Word · Excel · PowerPoint · image
                  </div>
                  <input
                    type="url"
                    autoFocus
                    value={url}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="Paste URL here"
                    style={{
                      marginTop: 2,
                      width: "min(100%, 380px)",
                      padding: "10px 14px",
                      background: "var(--s1)",
                      border: "1px solid var(--bdr)",
                      borderRadius: 999,
                      color: "var(--t1)",
                      fontFamily: "var(--font-sans)",
                      fontSize: 13,
                      textAlign: "center",
                      outline: "none",
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = "#60a5fa"}
                    onBlur={(e) => e.currentTarget.style.borderColor = "var(--bdr)"}
                  />
                  <div style={{ fontSize: 11, color: "var(--t3)", opacity: .7 }}>
                    Max 25 MB per file. Bigger? Paste a Drive / Dropbox link.
                  </div>
                </>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </label>
          ) : (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "14px 16px",
              background: "rgba(74,222,128,.08)",
              border: "1px solid rgba(74,222,128,.3)",
              borderRadius: 12,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(74,222,128,.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#4ade80" }}>
                <FileText size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {uploadedFile.filename}
                </div>
                <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 2 }}>
                  {(uploadedFile.size / 1024 / 1024).toFixed(1)} MB · uploaded
                </div>
              </div>
              <button
                onClick={clearFile}
                style={{ background: "transparent", border: "1px solid var(--bdr)", borderRadius: 8, padding: "6px 12px", color: "var(--t3)", fontSize: 12, cursor: "pointer" }}
              >Replace</button>
            </div>
          )}

          {/* Auto-detection + preview row — appears as soon as the user pastes
              a URL or uploads a file. One compact strip that says "we see
              what you dropped" instead of a separate thumbnail preview card. */}
          {(url.length > 10 || uploadedFile) && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "var(--t3)" }}>
              {detecting ? (
                <span>Looking at the link…</span>
              ) : (
                <>
                  <span style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    background: inf.kind === "video" ? "rgba(167,139,250,.14)" : inf.kind === "deck" ? "rgba(96,165,250,.14)" : inf.kind === "doc" || inf.kind === "pdf" ? "rgba(251,191,36,.14)" : "var(--s2)",
                    color: inf.kind === "video" ? "#a78bfa" : inf.kind === "deck" ? "#60a5fa" : inf.kind === "doc" || inf.kind === "pdf" ? "#fbbf24" : "var(--t2)",
                    fontWeight: 600,
                  }}>
                    {inf.label}
                  </span>
                  <span style={{ opacity: .7 }}>
                    {inf.kind === "unknown" ? "We'll still add it — link-only" : `Detected as ${inf.kind}`}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Thumbnail preview — smaller (120h, not 16:9 full) and only when
              we have something worth showing. */}
          {!uploadedFile && thumb && (
            <div style={{ aspectRatio: "16 / 9", width: "100%", maxHeight: 220, background: "var(--bg)", border: "1px solid var(--bdr)", borderRadius: 12, overflow: "hidden" }}>
              <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}

          {/* Title — pre-filled from og:title or filename, feels auto, not required */}
          <div>
            <label style={{ fontSize: 12, color: "var(--t3)", fontWeight: 500, display: "block", marginBottom: 6 }}>
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={uploadedFile || url ? "Edit if you want" : "What is this?"}
              style={{
                width: "100%", padding: "12px 14px",
                background: "var(--bg)", border: "1px solid var(--bdr)",
                borderRadius: 10, color: "var(--t1)",
                fontFamily: "var(--font-sans)", fontSize: 14, outline: "none",
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "#60a5fa"}
              onBlur={(e) => e.currentTarget.style.borderColor = "var(--bdr)"}
            />
          </div>

          {/* Tags — chip-style input with autocomplete from the existing tag
              cloud. Enter or comma commits; Backspace on empty input removes
              the last chip. Suggestions appear in a dropdown while typing. */}
          <div>
            <label style={{ fontSize: 12, color: "var(--t3)", fontWeight: 500, display: "block", marginBottom: 6 }}>
              Tags <span style={{ color: "var(--t4, #3e3e3e)" }}>(optional but helpful)</span>
            </label>
            <div style={{ position: "relative" }}>
              <div
                style={{
                  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6,
                  padding: "8px 10px",
                  background: "var(--bg)",
                  border: "1px solid var(--bdr)",
                  borderRadius: 10,
                  minHeight: 42,
                }}
                onClick={(e) => {
                  const input = e.currentTarget.querySelector("input");
                  if (input) input.focus();
                }}
              >
                {tags.map((t) => (
                  <span key={t} style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "3px 4px 3px 10px",
                    background: "rgba(96,165,250,.12)",
                    color: "#60a5fa", borderRadius: 999,
                    fontSize: 12, fontWeight: 500,
                  }}>
                    #{t}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeTag(t); }}
                      style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 2, display: "flex" }}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault(); addTag();
                    } else if (e.key === "Backspace" && !tagDraft && tags.length) {
                      setTags(tags.slice(0, -1));
                    }
                  }}
                  placeholder={tags.length === 0 ? "e.g. gtm, onboarding, research" : ""}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    fontSize: 13,
                    color: "var(--t1)",
                    fontFamily: "var(--font-sans)",
                    padding: "4px 0",
                  }}
                />
              </div>
              {/* Autocomplete — shows matching existing tags from the team's
                  cloud so users pick the canonical one instead of fragmenting. */}
              {draftMatches.length > 0 && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                  background: "var(--s1)",
                  border: "1px solid var(--bdr)",
                  borderRadius: 10,
                  padding: 6,
                  zIndex: 10,
                  boxShadow: "0 12px 32px rgba(0,0,0,.3)",
                }}>
                  {draftMatches.map((s) => (
                    <button
                      key={s.tag}
                      type="button"
                      onClick={() => addTag(s.tag)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        width: "100%", padding: "6px 10px",
                        background: "transparent", border: "none",
                        color: "var(--t1)", fontSize: 13, cursor: "pointer",
                        borderRadius: 6, textAlign: "left",
                        fontFamily: "var(--font-sans)",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "var(--s2)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <span>#{s.tag}</span>
                      <span style={{ color: "var(--t3)", fontSize: 11 }}>{s.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Description — collapsed by default. Most items don't need one. */}
          {showDescription ? (
            <div>
              <label style={{ fontSize: 12, color: "var(--t3)", fontWeight: 500, display: "block", marginBottom: 6 }}>
                Description
              </label>
              <input
                type="text"
                autoFocus
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One line about why this is here"
                style={{
                  width: "100%", padding: "12px 14px",
                  background: "var(--bg)", border: "1px solid var(--bdr)",
                  borderRadius: 10, color: "var(--t1)",
                  fontFamily: "var(--font-sans)", fontSize: 14, outline: "none",
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = "#60a5fa"}
                onBlur={(e) => e.currentTarget.style.borderColor = "var(--bdr)"}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              style={{
                alignSelf: "flex-start",
                padding: "4px 0",
                background: "transparent", border: "none",
                color: "var(--t3)", fontSize: 12, fontWeight: 500,
                cursor: "pointer", fontFamily: "var(--font-sans)",
                textDecoration: "underline", textUnderlineOffset: 3,
              }}
            >
              + Add description
            </button>
          )}

          {/* Supporting materials — optional list of additional files/links */}
          <div>
            <label style={{ fontSize: 12, color: "var(--t3)", fontWeight: 500, display: "block", marginBottom: 8 }}>
              Supporting materials <span style={{ color: "var(--t4, #3e3e3e)" }}>(optional)</span>
            </label>
            {attachments.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {attachments.map((a, i) => (
                  <AttachmentStagedRow
                    key={i}
                    value={a}
                    onChange={(patch) => updateAttachment(i, patch)}
                    onUploadFile={(f) => uploadAttachmentFile(i, f)}
                    onRemove={() => removeAttachment(i)}
                  />
                ))}
              </div>
            )}

            {/* Multi-file drop zone — accepts drag-drop OR click-to-pick multiple. */}
            <label
              onDragEnter={(e) => { e.preventDefault(); setAttDropOver(true); }}
              onDragOver={(e) => { e.preventDefault(); setAttDropOver(true); }}
              onDragLeave={() => setAttDropOver(false)}
              onDrop={onAttDrop}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "16px 14px",
                background: attDropOver ? "rgba(96,165,250,.1)" : "var(--bg)",
                border: `1.5px dashed ${attDropOver ? "#60a5fa" : "var(--bdr)"}`,
                borderRadius: 10,
                cursor: "pointer",
                transition: "all .15s",
                color: "var(--t2)",
                fontSize: 13,
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              <Plus size={14} style={{ color: "var(--t3)" }} />
              <span>
                {attDropOver
                  ? "Drop to attach"
                  : attachments.length === 0
                    ? "Drop files here, or click to pick — multiple OK"
                    : "Drop more files, or click to pick"}
              </span>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp"
                onChange={(e) => { uploadMultipleAttachmentFiles(e.target.files); e.target.value = ""; }}
                style={{ display: "none" }}
              />
            </label>

            {/* Secondary: add a URL-only attachment row (for Google Drive / Notion / etc.) */}
            <button
              type="button"
              onClick={addAttachmentUrl}
              style={{
                marginTop: 8,
                padding: "6px 12px",
                background: "transparent",
                border: "none",
                borderRadius: 999,
                color: "var(--t3)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Link2 size={12} /> Or paste a URL instead
            </button>

            {attachments.length > 0 && (
              <p style={{ fontSize: 11, color: "var(--t4, #3e3e3e)", marginTop: 8 }}>
                Attach reading material, extra decks, reference docs — anything to support the main item.
              </p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 6 }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 18px",
                background: "transparent",
                border: "1px solid var(--bdr)",
                borderRadius: 999,
                color: "var(--t2)",
                fontFamily: "var(--font-sans)",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            {(() => {
              const canSubmit = !submitting && title.trim() && (uploadedFile || url.trim());
              return (
                <button
                  onClick={submit}
                  disabled={!canSubmit}
                  style={{
                    padding: "10px 22px",
                    background: canSubmit ? "var(--t1)" : "var(--s3)",
                    border: "none",
                    borderRadius: 999,
                    color: canSubmit ? "var(--bg)" : "var(--t3)",
                    fontFamily: "var(--font-sans)",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: canSubmit ? "pointer" : "not-allowed",
                  }}
                >
                  {submitting ? "Adding…" : "Add"}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ Staged attachment row (in AddModal) ═══════════════════════ */

function AttachmentStagedRow({ value, onChange, onUploadFile, onRemove }) {
  const filledInf = useMemo(() => inferContent(value.content_url || ""), [value.content_url]);
  const isUploaded = value.content_url && value.content_url.startsWith("/api/pitch-decks/file/");
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      background: "var(--bg)",
      border: "1px solid var(--bdr)",
      borderRadius: 10,
    }}>
      {isUploaded || value.filename ? (
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: "rgba(74,222,128,.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#4ade80", flexShrink: 0,
        }}>
          <FileText size={15} />
        </div>
      ) : value.content_url ? (
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: filledInf.kind === "video" ? "rgba(167,139,250,.14)" : filledInf.kind === "deck" ? "rgba(96,165,250,.14)" : "var(--s2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: filledInf.kind === "video" ? "#a78bfa" : filledInf.kind === "deck" ? "#60a5fa" : "var(--t3)", flexShrink: 0,
        }}>
          {filledInf.kind === "video" ? <Play size={14} /> : filledInf.kind === "deck" ? <Layers size={14} /> : <Link2 size={14} />}
        </div>
      ) : (
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: "var(--s2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--t4, #3e3e3e)", flexShrink: 0,
        }}>
          <Link2 size={14} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        {value.filename ? (
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value.filename}
          </div>
        ) : (
          <input
            type="url"
            value={value.content_url || ""}
            onChange={(e) => onChange({ content_url: e.target.value })}
            placeholder="Paste URL or upload file"
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              color: "var(--t1)",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              outline: "none",
              padding: 0,
            }}
          />
        )}
        <input
          type="text"
          value={value.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Label (optional)"
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            color: "var(--t3)",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            outline: "none",
            padding: 0,
            marginTop: 2,
          }}
        />
      </div>

      {!value.filename && !value._uploading && (
        <label style={{
          padding: "6px 10px",
          background: "var(--s2)",
          border: "1px solid var(--bdr)",
          borderRadius: 8,
          color: "var(--t3)",
          fontSize: 11,
          cursor: "pointer",
          fontWeight: 500,
        }}>
          Upload
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp"
            onChange={(e) => onUploadFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
        </label>
      )}
      {value._uploading && (
        <span style={{ fontSize: 12, color: "var(--t3)" }}>Uploading…</span>
      )}
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--t3)",
          cursor: "pointer",
          padding: 4,
          display: "flex",
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

